import { computed, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { BEStatus } from '@qtek/core/api-core';
import {
  BaseWebsocketMessage,
  SubWebsocketMessageRequest,
  WsEntityMessage,
} from '@qtek/core/websockets-core';
import { NormalSnackBarData, SnackBarService } from '@qtek/libs/snack-bar-core';
import { EntityMeta, Item, UserApproval } from '@qtek/shared/models';
import {
  catchError,
  filter,
  map,
  mergeMap,
  of,
  pipe,
  Subject,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import { ItemService } from './item.service';

export interface ItemsState {
  allItems: Record<string, Item[]>;
  itemsMeta: EntityMeta;
  itemsLoading: Record<string, boolean>;
}

const initialState: ItemsState = {
  allItems: {},
  itemsMeta: new EntityMeta(),
  itemsLoading: {},
};

/** Payload accepted by {@link ItemStore.connect} — a subscribe frame minus the
 * `ent`/`op` the service fills in. Callers pass `mysid`, `view`, and either a
 * `q` filter or (v2) a named `view`. */
type ConnectItemsPayload = Omit<SubWebsocketMessageRequest, 'ent' | 'op'>;

/**
 * Backend `ReplyStatus` as consumed by this store: status/attrs (via
 * {@link BEStatus}) plus the grid `meta` this store reads off `query` replies.
 */
interface ItemWsStatus extends BEStatus {
  meta?: EntityMeta;
}

type ItemQueryMessage = WsEntityMessage<Item[], ItemWsStatus, 'query'>;

type ItemGetMessage = WsEntityMessage<Item | Item[], ItemWsStatus, 'get'>;

type ItemMutationMessage = WsEntityMessage<
  Item,
  ItemWsStatus,
  'ins' | 'upd' | 'del'
>;

/**
 * A model reply for the `Item` entity, discriminated by `op`. Mirrors the
 * backend `WsPayload<ItemApi>` envelope: `query` carries an array, the
 * single-item ops carry one `Item` (`get` handled defensively as either).
 */
type ItemWsMessage = ItemQueryMessage | ItemGetMessage | ItemMutationMessage;

export const ItemStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withProps(() => ({
    update$: new Subject<{ res: Item; mysid: string }>(),
    updateFailure$: new Subject<{ mysid?: string }>(),
    create$: new Subject<{ res: Item; mysid: string }>(),
    createFailure$: new Subject<unknown>(),
    getItem$: new Subject<{ res: Item; mysid: string }>(),
    getItemFailure$: new Subject<unknown>(),
    getItems$: new Subject<{ res: Item[]; meta: EntityMeta; mysid: string }>(),
    getItemsFailure$: new Subject<unknown>(),
    sendNdaEvent$: new Subject<unknown>(),
    sendNdaEventFailure$: new Subject<unknown>(),
    bulkCreate$: new Subject<Item[]>(),
    bulkCreateFailure$: new Subject<unknown>(),
  })),
  withMethods(
    (
      store,
      service = inject(ItemService),
      snackBar = inject(SnackBarService)
    ) => {
      const disconnect$ = new Subject<string>();
      const pendingGetMysidById = new Map<string, string>();

      return {
        connect: rxMethod<ConnectItemsPayload>(
          pipe(
            mergeMap((payload: ConnectItemsPayload) => {
              patchState(store, state => ({
                itemsLoading: { ...state.itemsLoading, [payload.mysid]: true },
              }));
              const messageFilter =
                payload.mysid === 'task_form_related_deal_lookup'
                  ? (message: BaseWebsocketMessage) =>
                      message.ent === service.entityName &&
                      Boolean(message.op) &&
                      (message.mysid === payload.mysid || message.op === 'get')
                  : undefined;

              return service.subscribe(payload, messageFilter).pipe(
                takeUntil(disconnect$.pipe(filter(id => id === payload.mysid))),
                tap((message: BaseWebsocketMessage) => {
                  const data = message as ItemWsMessage;
                  if (!data.op) return;

                  switch (data.op) {
                    case 'query': {
                      const { res, sts, mysid } = data;
                      if (mysid === 'task_form_related_deal_lookup') {
                        patchState(store, state => ({
                          itemsLoading: {
                            ...state.itemsLoading,
                            [mysid]: false,
                          },
                        }));
                        return;
                      }
                      if (res) {
                        patchState(store, state => ({
                          allItems: { ...state.allItems, [mysid]: res ?? [] },
                          itemsMeta: sts?.meta ?? state.itemsMeta,
                          itemsLoading: {
                            ...state.itemsLoading,
                            [mysid]: false,
                          },
                        }));
                        store.getItems$.next({
                          res: res ?? [],
                          meta: sts?.meta,
                          mysid,
                        });
                      } else {
                        patchState(store, state => ({
                          itemsLoading: {
                            ...state.itemsLoading,
                            [mysid]: false,
                          },
                        }));
                        store.getItemsFailure$.next(sts);
                      }
                      break;
                    }
                    case 'get': {
                      const { res, sts, mysid } = data;
                      const item = Array.isArray(res) ? res[0] : res;
                      const responseId = data.id ?? item?._id;
                      const mysidFromPending =
                        responseId && pendingGetMysidById.get(responseId);
                      if (responseId) pendingGetMysidById.delete(responseId);
                      const mysidFinal = mysidFromPending ?? mysid;

                      if (item && mysidFinal) {
                        patchState(store, state => {
                          const existing = state.allItems[mysidFinal] ?? [];
                          const idx = existing.findIndex(
                            i => i._id === item._id
                          );
                          const updated =
                            idx >= 0
                              ? existing.map((i, j) => (j === idx ? item : i))
                              : [...existing, item];
                          return {
                            allItems: {
                              ...state.allItems,
                              [mysidFinal]: updated,
                            },
                          };
                        });
                        store.getItem$.next({ res: item, mysid: mysidFinal });
                      } else if (!item) {
                        store.getItemFailure$.next(sts);
                      }
                      break;
                    }
                    case 'ins': {
                      const { res, sts, mysid } = data;
                      if (res) {
                        patchState(store, state => ({
                          allItems: {
                            ...state.allItems,
                            [mysid]: [...(state.allItems[mysid] ?? []), res],
                          },
                        }));
                        store.create$.next({ res, mysid });
                        snackBar.showSnackBar({
                          type: 'NormalMessage',
                          message: 'GNR_ITM_CREATED',
                        } as NormalSnackBarData);
                      } else {
                        store.createFailure$.next(sts);
                      }
                      break;
                    }
                    case 'upd': {
                      const { res, mysid } = data;
                      if (res) {
                        patchState(store, state => {
                          const arr = (state.allItems[mysid] ?? []).slice();
                          const idx = arr.findIndex(i => i._id === res._id);
                          if (idx >= 0) {
                            const merged = { ...arr[idx], ...res };
                            // v2 renamed the V1 parent pointer `pid` -> `parent`.
                            if ('parent' in arr[idx] && !('parent' in res)) {
                              delete merged.parent;
                            }
                            arr[idx] = merged;
                          }
                          return {
                            allItems: { ...state.allItems, [mysid]: arr },
                          };
                        });
                        store.update$.next({ res, mysid });
                        snackBar.showSnackBar({
                          type: 'NormalMessage',
                          message: 'GNR_ITM_UPDATED',
                        } as NormalSnackBarData);
                      } else {
                        store.updateFailure$.next({ mysid });
                      }
                      break;
                    }
                    case 'del': {
                      const { res, mysid } = data;
                      if (res) {
                        patchState(store, state => ({
                          allItems: {
                            ...state.allItems,
                            [mysid]: (state.allItems[mysid] ?? []).filter(
                              i => i._id !== res._id
                            ),
                          },
                        }));
                        snackBar.showSnackBar({
                          type: 'NormalMessage',
                          message: 'GNR_ITM_DELETED',
                        } as NormalSnackBarData);
                      }
                      break;
                    }
                  }
                })
              );
            })
          )
        ),

        disconnect(mysid: string): void {
          disconnect$.next(mysid);
          patchState(store, state => {
            const { [mysid]: _a, ...allItems } = state.allItems;
            const { [mysid]: _b, ...itemsLoading } = state.itemsLoading;
            return { allItems, itemsLoading };
          });
        },

        getItems(payload: Parameters<typeof service.getItems>[0]): void {
          service.getItems(payload);
        },

        getItemById(payload: {
          id: string;
          view?: string;
          mysid?: string;
        }): void {
          if (payload.id && payload.mysid) {
            pendingGetMysidById.set(payload.id, payload.mysid);
          }
          service.getItemById(payload);
        },

        createItem(payload: Item): void {
          service.createItem({ res: payload });
        },

        update(payload: { payload: Item; id: string; mysid?: string }): void {
          service.updateItem({
            res: payload.payload,
            id: payload.id,
            mysid: payload.mysid,
          });
        },

        deleteItem(payload: number | string): void {
          service.deleteItem({ id: payload });
        },

        mintItem: rxMethod<number>(
          pipe(
            switchMap(seqno =>
              service.mintItem(seqno).pipe(catchError(() => of(null)))
            )
          )
        ),

        sendNdaEvent: rxMethod<{ parId: string; userApproval: UserApproval }>(
          pipe(
            switchMap(({ parId, userApproval }) =>
              service.sendNdaEvent(parId, userApproval).pipe(
                map(({ res }) => {
                  store.sendNdaEvent$.next(res);
                  return res;
                }),
                catchError(({ sts }) => {
                  store.sendNdaEventFailure$.next(sts);
                  return of(null);
                })
              )
            )
          )
        ),

        bulkCreate: rxMethod<Item[]>(
          pipe(
            mergeMap(items =>
              service.bulkCreateItems(items).pipe(
                map(({ res }) => {
                  store.bulkCreate$.next(res ?? []);
                  return res;
                }),
                catchError(({ sts }) => {
                  store.bulkCreateFailure$.next(sts);
                  return of(null);
                })
              )
            )
          )
        ),

        itemsByMysid(mysid: string) {
          return computed(() => store.allItems()[mysid] ?? []);
        },

        loadingByMysid(mysid: string) {
          return computed(() => store.itemsLoading()[mysid] ?? false);
        },
      };
    }
  ),
  withComputed(({ itemsMeta }) => ({
    currentItemsMeta: computed(() => itemsMeta()),
  }))
);
